<template>
	<nav
		aria-label="Paginanavigatie"
	>
		<ul class="list-none flex justify-center gap-2 flex-wrap">
			<li class="pr-2 sm:pr-8">
				<AtomLink
					:url="getPageUrl(getPreviousPage())"
					:title="`Pagina ${getPreviousPage()}`"
					:tabindex="isCurrent(1) ? -1 : undefined"
					:aria-disabled="isCurrent(1)"
					class="text-functional-size-md size-12 flex justify-center items-center rounded-full"
					:class="{
						'text-gray-400 pointer-events-none': isCurrent(1),
						'text-gray-500 shadow bg-white hover:bg-gray-100': !isCurrent(1),
						'transition-colors duration-200': !isCurrent(1),
					}"
				>
					<span class="sr-only">Vorige pagina</span>
					<AtomFontAwesome
						class="size-6"
						icon="fa-angle-left"
					/>
				</AtomLink>
			</li>
			<li
				v-if="getMinPageInRow() > 1"
				:data-test-jump="getMinPageInRow() > 2 ? true : undefined"
				:class="{
					'pr-2 sm:pr-8': getMinPageInRow() > 2,
				}"
			>
				<AtomLink
					:url="getPageUrl(1)"
					:title="`Pagina ${1}`"
					:tabindex="isCurrent(1) ? -1 : undefined"
					:aria-disabled="isCurrent(1)"
					class="text-functional-size-md size-12 flex justify-center items-center rounded-full"
					:class="{
						'text-gray-400 pointer-events-none': isCurrent(1),
						'text-gray-500 shadow bg-white hover:bg-gray-100': !isCurrent(1),
						'transition-colors duration-200': !isCurrent(1),
					}"
				>
					<span class="sr-only">Pagina</span>
					1
				</AtomLink>
			</li>
			<li
				v-for="index in getPagesInRow()"
				:key="`pagination-item-${index}`"
			>
				<AtomLink
					:url="getPageUrl(index)"
					:title="`Pagina ${index}`"
					:tabindex="isCurrent(index) ? -1 : undefined"
					:aria-disabled="isCurrent(index)"
					:aria-current="isCurrent(index) ? 'page' : undefined"
					class="text-functional-size-md size-12 flex justify-center items-center rounded-full text-gray-500"
					:class="{
						'pointer-events-none': isCurrent(index),
						'shadow bg-white hover:bg-gray-100': !isCurrent(index),
						'transition-colors duration-200': !isCurrent(index),
					}"
				>
					<span class="sr-only">Pagina</span>
					{{ index }}
				</AtomLink>
			</li>
			<li
				v-if="getMaxPageInRow() < maxPage"
				:class="{
					'pl-2 sm:pl-8': getMaxPageInRow() + 1 < maxPage,
				}"
			>
				<AtomLink
					:url="getPageUrl(maxPage)"
					:title="`Pagina ${maxPage}`"
					:tabindex="isCurrent(maxPage) ? -1 : undefined"
					:aria-disabled="isCurrent(maxPage)"
					class="text-functional-size-md size-12 flex justify-center items-center rounded-full"
					:class="{
						'text-gray-400 pointer-events-none': isCurrent(maxPage),
						'text-gray-500 shadow bg-white hover:bg-gray-100': !isCurrent(maxPage),
						'transition-colors duration-200': !isCurrent(maxPage),
					}"
				>
					<span class="sr-only">Pagina</span>
					{{ maxPage }}
				</AtomLink>
			</li>
			<li class="pl-2 sm:pl-8">
				<AtomLink
					:url="getPageUrl(getNextPage())"
					:title="`Pagina ${getNextPage()}`"
					:tabindex="isCurrent(maxPage) ? -1 : undefined"
					:aria-disabled="isCurrent(maxPage)"
					class="text-functional-size-md size-12 flex justify-center items-center rounded-full"
					:class="{
						'text-gray-400 pointer-events-none': isCurrent(maxPage),
						'text-gray-500 shadow bg-white hover:bg-gray-100': !isCurrent(maxPage),
						'transition-colors duration-200': !isCurrent(maxPage),
					}"
					@click.prevent="() => refreshNuxtData()"
				>
					<span class="sr-only">Volgende pagina</span>
					<AtomFontAwesome
						class="size-6"
						icon="fa-angle-right"
					/>
				</AtomLink>
			</li>
		</ul>
	</nav>
</template>

<script setup lang="ts">
import qs from 'qs';
import type { PaginationObjectType } from '#base/core/types/PaginationObjectType';

const props = defineProps<{
	pagination: PaginationObjectType;
}>();

const route = useRoute();

const getPageUrl = (page: number) => {
	const pageQuery: Record<string, unknown> = {
		...route.query,
	};

	if (page === 1) {
		return route.path;
	}

	pageQuery.page = page;

	return `${route.path}?${qs.stringify(pageQuery)}`;
};

const paginationStart = computed(() => route.query.page ? Number(route.query.page) - 1 : 0);

const isCurrent = (index: number) => {
	return paginationStart.value + 1 === index;
};

const getPreviousPage = () => {
	const previousPage = paginationStart.value;
	return previousPage < 1 ? 1 : previousPage;
};

const maxPage = Math.ceil(props.pagination.total / props.pagination.limit);

const getNextPage = () => {
	const nextPage = paginationStart.value + 2;
	return nextPage > maxPage ? maxPage : nextPage;
};

const getMaxPageInRow = () => {
	const maxPageInRow = maxPage + 3;

	return maxPageInRow > maxPage ? maxPage : maxPageInRow;
};

const getMinPageInRow = () => {
	const minPage = paginationStart.value - 1;

	return minPage < 1 ? 1 : minPage;
};

const getPagesInRow = () => {
	const pages = [];

	for (let i = getMinPageInRow(); i <= getMaxPageInRow(); i++) {
		pages.push(i);
	}

	return pages;
};
</script>
